import { useEffect, useState, type FormEvent } from 'react';
import api, { ApiError } from '../../lib/apiClient';
import { IconButton } from '../../components/IconButton';
import { IconEdit } from '../../components/icons';
import { Modal } from '../../components/Modal';

interface MappingRow {
  id: number;
  transaction_type: string;
  label: string;
  chart_of_account_id: number | null;
  chart_of_account: { id: number; code: string; name: string } | null;
  is_active: boolean;
}

const SUSPENSE_TYPES = ['benefit_return_to_suspense', 'benefit_reissue_from_suspense'];

export function GlAccountMappings() {
  const [items, setItems] = useState<MappingRow[] | null>(null);
  const [chartOfAccounts, setChartOfAccounts] = useState<{ id: number; code: string; name: string }[]>([]);
  const [editing, setEditing] = useState<MappingRow | null>(null);
  const [form, setForm] = useState({ chart_of_account_id: '' as number | '', is_active: false });
  const [errors, setErrors] = useState<Record<string, string[]>>({});

  const load = () => api.get<MappingRow[]>('/gl-account-mappings').then((res) => setItems(res.data));

  useEffect(() => {
    load();
    api.get<{ id: number; code: string; name: string }[]>('/chart-of-accounts').then((res) => setChartOfAccounts(res.data));
  }, []);

  const openEdit = (item: MappingRow) => {
    setForm({ chart_of_account_id: item.chart_of_account_id ?? '', is_active: item.is_active });
    setErrors({});
    setEditing(item);
  };

  const save = async (e: FormEvent) => {
    e.preventDefault();
    if (!editing) return;
    try {
      await api.put(`/gl-account-mappings/${editing.id}`, {
        chart_of_account_id: form.chart_of_account_id || null,
        is_active: form.is_active,
      });
      setEditing(null);
      load();
    } catch (err) {
      setErrors(err instanceof ApiError && err.errors ? err.errors : { general: ['Save failed.'] });
    }
  };

  return (
    <>
      <header className="page-header">
        <h1>Account Mappings</h1>
      </header>
      <p className="muted">
        Which chart-of-accounts code each automated GL transaction type posts against. A type stays inactive (and is
        skipped by the daily batch) until an account is configured here.
      </p>

      <div className="card">
        <div className="table-scroll">
          <table>
            <thead>
              <tr>
                <th>Transaction Type</th>
                <th>Mapped Account</th>
                <th>Active</th>
                <th></th>
              </tr>
            </thead>
            <tbody>
              {items?.map((item) => (
                <tr key={item.id}>
                  <td>{item.label}</td>
                  <td className="muted">
                    {SUSPENSE_TYPES.includes(item.transaction_type)
                      ? 'Per-claim snapshot (see the disbursing claim\'s own suspense account)'
                      : item.chart_of_account
                        ? `${item.chart_of_account.code} — ${item.chart_of_account.name}`
                        : 'Not set'}
                  </td>
                  <td className="muted">{item.is_active ? 'Yes' : 'No'}</td>
                  <td className="right">
                    <IconButton icon={IconEdit} label="Edit" onClick={() => openEdit(item)} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {editing && (
        <Modal title={`Edit mapping — ${editing.label}`} onClose={() => setEditing(null)}>
          <form onSubmit={save} className="form-grid">
            {errors.general && <div className="alert alert-error span-2">{errors.general[0]}</div>}
            {SUSPENSE_TYPES.includes(editing.transaction_type) ? (
              <p className="muted span-2">
                This type always posts against the specific benefit claim's own snapshotted suspense account, not a
                shared mapping — only the active flag below applies here.
              </p>
            ) : (
              <label className="span-2">
                Chart of account
                <select
                  value={form.chart_of_account_id}
                  onChange={(e) => setForm({ ...form, chart_of_account_id: e.target.value ? Number(e.target.value) : '' })}
                >
                  <option value="">None</option>
                  {chartOfAccounts.map((a) => (
                    <option key={a.id} value={a.id}>
                      {a.code} — {a.name}
                    </option>
                  ))}
                </select>
                {errors.chart_of_account_id && <small className="error">{errors.chart_of_account_id[0]}</small>}
              </label>
            )}
            <label className="span-2 checkbox-label">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
              />
              Active
            </label>
            <div className="form-actions span-2">
              <button type="button" className="btn btn-ghost" onClick={() => setEditing(null)}>
                Cancel
              </button>
              <button className="btn btn-primary">Save</button>
            </div>
          </form>
        </Modal>
      )}
    </>
  );
}
